import React, { useState, useEffect } from 'react';
import TailwindModal, { TailwindModalHeader, TailwindModalBody } from './TailwindModal';
import { Eye, EyeOff, Check, CircleAlert, Smartphone, Lock } from 'lucide-react';
import ApiClient from '../ApiClient';
import { notify } from '../utils/ToastUtils';

const ChangePasswordModal = ({ isOpen, toggle, is2FAEnabled, onSuccess }) => {
    // Group 1: Form field values
    const [passwords, setPasswords] = useState({
        current: '',
        new: '',
        confirm: '',
        twoFactorCode: ''
    });

    // Group 2: Password visibility toggles
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });

    // Group 3: Submission & Error status
    const [status, setStatus] = useState({
        isSubmitting: false,
        errorMessage: ''
    });

    // Reset state when opened
    useEffect(() => {
        if (isOpen) {
            setPasswords({ current: '', new: '', confirm: '', twoFactorCode: '' });
            setShowPasswords({ current: false, new: false, confirm: false });
            setStatus({ isSubmitting: false, errorMessage: '' });
        }
    }, [isOpen]);

    const handlePasswordChange = (field, value) => {
        setPasswords(prev => ({ ...prev, [field]: value }));
        if (status.errorMessage) {
            setStatus(prev => ({ ...prev, errorMessage: '' }));
        }
    };

    const toggleShowPassword = (field) => {
        setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
    };

    // Password criteria
    const criteria = {
        length: passwords.new.length >= 8,
        case: /[a-z]/.test(passwords.new) && /[A-Z]/.test(passwords.new),
        number: /[0-9]/.test(passwords.new),
        special: /[!@#\$%\^&\*]/.test(passwords.new)
    };
    
    const score = Object.values(criteria).filter(Boolean).length;
    let strengthText = 'Weak';
    let strengthColor = 'bg-red-500';
    if (score === 4) {
        strengthText = 'Strong';
        strengthColor = 'bg-emerald-500';
    } else if (score >= 2) {
        strengthText = 'Fair';
        strengthColor = 'bg-amber-500';
    }

    const passwordsMatch = passwords.new && passwords.confirm && passwords.new === passwords.confirm;

    // Active error message (API error or live validation issue)
    const activeErrorMessage = status.errorMessage || (
        passwords.confirm && !passwordsMatch 
            ? "New passwords do not match." 
            : (passwords.current && passwords.new && passwords.current === passwords.new 
                ? "New password cannot be the same as current password." 
                : "")
    );

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();

        if (!passwords.current) {
            setStatus(prev => ({ ...prev, errorMessage: 'Current password is required.' }));
            return;
        }

        if (!passwords.new) {
            setStatus(prev => ({ ...prev, errorMessage: 'New password is required.' }));
            return;
        }

        if (score < 4) {
            setStatus(prev => ({ ...prev, errorMessage: 'New password does not meet all complexity requirements.' }));
            return;
        }

        if (!passwords.confirm) {
            setStatus(prev => ({ ...prev, errorMessage: 'Please confirm your new password.' }));
            return;
        }

        if (passwords.new !== passwords.confirm) {
            setStatus(prev => ({ ...prev, errorMessage: 'New passwords do not match.' }));
            return;
        }

        if (passwords.current === passwords.new) {
            setStatus(prev => ({ ...prev, errorMessage: 'New password cannot be the same as current password.' }));
            return;
        }

        if (is2FAEnabled) {
            if (!passwords.twoFactorCode) {
                setStatus(prev => ({ ...prev, errorMessage: 'Two-Factor Authentication code is required.' }));
                return;
            }
            if (passwords.twoFactorCode.length !== 6) {
                setStatus(prev => ({ ...prev, errorMessage: 'Authentication code must be 6 digits.' }));
                return;
            }
        }

        submitPasswordChange();
    };

    const submitPasswordChange = async () => {
        setStatus(prev => ({ ...prev, isSubmitting: true, errorMessage: '' }));
        try {
            await ApiClient.post(`${process.env.REACT_APP_BASE_URL}/user/changePassword`, {
                currentPassword: passwords.current,
                password: passwords.new,
                twoFactorCode: is2FAEnabled ? passwords.twoFactorCode : undefined
            });
            
            notify.success("Password updated successfully");
            if (onSuccess) onSuccess();
            toggle();
        } catch (error) {
            console.error(error);
            let msg = error?.response?.data?.message || error?.response?.data?.error;
            
            // Avoid printing raw HTML error responses
            if (!msg && typeof error?.response?.data === 'string' && !error.response.data.includes('<html') && !error.response.data.includes('<!DOCTYPE')) {
                msg = error.response.data;
            }
            if (!msg) {
                msg = error?.response?.status === 404 
                    ? "Service endpoint not found (404)." 
                    : (error?.message || "Failed to update password");
            }
            setStatus(prev => ({
                ...prev,
                errorMessage: msg
            }));
        } finally {
            setStatus(prev => ({ ...prev, isSubmitting: false }));
        }
    };

    const renderBars = () => {
        return (
            <div className="flex gap-1 flex-1 h-1">
                {[1, 2, 3, 4].map(idx => (
                    <div 
                        key={idx} 
                        className={`flex-1 rounded-full ${idx <= score ? strengthColor : 'bg-slate-200'}`}
                    />
                ))}
            </div>
        );
    };

    return (
        <TailwindModal isOpen={isOpen} toggle={toggle} maxWidth="md">
            <TailwindModalHeader 
                toggle={toggle} 
                icon={<Lock size={16} strokeWidth={2.2} />}
                title="Change Password"
                subtitle="Update your password to stay secure."
            />
            <TailwindModalBody className="p-3.5 sm:p-4">
                <form onSubmit={handleSubmit} className="space-y-2.5">
                    {/* Current Password */}
                    <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-0.5">Current Password <span className="text-red-500">*</span></label>
                        <div className="relative">
                            <input 
                                type={showPasswords.current ? "text" : "password"} 
                                value={passwords.current}
                                onChange={(e) => handlePasswordChange('current', e.target.value)}
                                className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-shadow"
                                placeholder="Enter Current password"
                            />
                            <button type="button" onClick={() => toggleShowPassword('current')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                                {showPasswords.current ? <EyeOff size={15} /> : <Eye size={15} />}
                            </button>
                        </div>
                    </div>

                    {/* New Password */}
                    <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-0.5">New Password <span className="text-red-500">*</span></label>
                        <div className="relative mb-1">
                            <input 
                                type={showPasswords.new ? "text" : "password"} 
                                value={passwords.new}
                                onChange={(e) => handlePasswordChange('new', e.target.value)}
                                className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-shadow"
                                placeholder="Enter New password"
                            />
                            <button type="button" onClick={() => toggleShowPassword('new')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                                {showPasswords.new ? <EyeOff size={15} /> : <Eye size={15} />}
                            </button>
                        </div>
                        
                        {passwords.new && (
                            <div className="flex items-center gap-2 mb-1">
                                {renderBars()}
                                <span className={`text-[11px] font-medium w-12 text-right ${strengthText === 'Strong' ? 'text-emerald-600' : strengthText === 'Fair' ? 'text-amber-600' : 'text-red-600'}`}>
                                    {strengthText}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Confirm Password */}
                    <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-0.5">Confirm New Password <span className="text-red-500">*</span></label>
                        <div className="relative">
                            <input 
                                type={showPasswords.confirm ? "text" : "password"} 
                                value={passwords.confirm}
                                onChange={(e) => handlePasswordChange('confirm', e.target.value)}
                                className={`w-full px-3 py-1.5 bg-white border rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-shadow ${passwords.confirm && !passwordsMatch ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' : 'border-slate-300'}`}
                                placeholder="Enter Confirm New password"
                            />
                            <button type="button" onClick={() => toggleShowPassword('confirm')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                                {showPasswords.confirm ? <EyeOff size={15} /> : <Eye size={15} />}
                            </button>
                        </div>
                    </div>

                    {/* Requirements */}
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5">
                        <p className="text-[11px] font-semibold text-slate-700 mb-1.5">
                            Password requirements
                        </p>

                        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                            {/* Length */}
                            <div className="flex items-center gap-1.5 text-[11px]">
                                {criteria.length ? (
                                    <div className="flex items-center justify-center w-3.5 h-3.5 rounded-full bg-emerald-100 shrink-0">
                                        <Check size={10} className="text-emerald-600" strokeWidth={2.5} />
                                    </div>
                                ) : (
                                    <div className="w-3.5 h-3.5 rounded-full border border-slate-300 bg-white shrink-0" />
                                )}

                                <span className={criteria.length ? "text-slate-700" : "text-slate-500"}>
                                    At least 8 characters
                                </span>
                            </div>

                            {/* Uppercase + Lowercase */}
                            <div className="flex items-center gap-1.5 text-[11px]">
                                {criteria.case ? (
                                    <div className="flex items-center justify-center w-3.5 h-3.5 rounded-full bg-emerald-100 shrink-0">
                                        <Check size={10} className="text-emerald-600" strokeWidth={2.5} />
                                    </div>
                                ) : (
                                    <div className="w-3.5 h-3.5 rounded-full border border-slate-300 bg-white shrink-0" />
                                )}

                                <span className={criteria.case ? "text-slate-700" : "text-slate-500"}>
                                    Uppercase & lowercase
                                </span>
                            </div>

                            {/* Number */}
                            <div className="flex items-center gap-1.5 text-[11px]">
                                {criteria.number ? (
                                    <div className="flex items-center justify-center w-3.5 h-3.5 rounded-full bg-emerald-100 shrink-0">
                                        <Check size={10} className="text-emerald-600" strokeWidth={2.5} />
                                    </div>
                                ) : (
                                    <div className="w-3.5 h-3.5 rounded-full border border-slate-300 bg-white shrink-0" />
                                )}

                                <span className={criteria.number ? "text-slate-700" : "text-slate-500"}>
                                    At least one number
                                </span>
                            </div>

                            {/* Special Character */}
                            <div className="flex items-center gap-1.5 text-[11px]">
                                {criteria.special ? (
                                    <div className="flex items-center justify-center w-3.5 h-3.5 rounded-full bg-emerald-100 shrink-0">
                                        <Check size={10} className="text-emerald-600" strokeWidth={2.5} />
                                    </div>
                                ) : (
                                    <div className="w-3.5 h-3.5 rounded-full border border-slate-300 bg-white shrink-0" />
                                )}

                                <span
                                    className={criteria.special ? "text-slate-700" : "text-slate-500"}
                                    title="A special character such as !, @, #, $, %, ^, &, or *"
                                >
                                    One special character
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* 2FA Code (Only if 2FA is Enabled) */}
                    {is2FAEnabled && (
                        <div>
                            <label className="block text-xs font-semibold text-slate-700 mb-0.5">
                                Two-Factor Authentication Code <span className="text-slate-400 font-normal text-[11px]">(using authenticator app)</span> <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <Smartphone size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input 
                                    type="text" 
                                    inputMode="numeric"
                                    maxLength={6} 
                                    value={passwords.twoFactorCode} 
                                    onChange={(e) => handlePasswordChange('twoFactorCode', e.target.value.replace(/\D/g, ''))}
                                    className="w-full pl-8 pr-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs sm:text-sm tracking-widest focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-shadow font-mono"
                                    placeholder="Enter 6-digit 2FA code"
                                />
                            </div>
                        </div>
                    )}

                    {/* Error Alert Above Buttons */}
                    {activeErrorMessage && (
                        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-2 text-xs font-medium text-red-600 shadow-sm mt-1">
                            <CircleAlert size={15} className="shrink-0 text-red-500" />
                            <span>{activeErrorMessage}</span>
                        </div>
                    )}

                    {/* Footer buttons */}
                    <div className="flex items-center justify-end gap-2.5 pt-1">
                        <button 
                            type="button"
                            onClick={toggle}
                            className="px-3.5 py-1.5 border border-slate-200 text-slate-700 rounded-lg font-medium text-xs sm:text-sm hover:bg-slate-50 transition-colors bg-white shadow-sm"
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            disabled={status.isSubmitting}
                            className="px-4 py-1.5 bg-blue-600 text-white rounded-lg font-medium text-xs sm:text-sm hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-50 flex items-center justify-center min-w-[130px]"
                        >
                            {status.isSubmitting ? <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span> : null}
                            {status.isSubmitting ? "Updating..." : "Update Password"}
                        </button>
                    </div>
                </form>
            </TailwindModalBody>
        </TailwindModal>
    );
};

export default ChangePasswordModal;
